import {Box, Button, Typography} from '@mui/material';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAppDispatch} from '../../store/hooks';
import {processSessionVideoAsync} from '../../store/sessionVideoSlice';
import {adminChangeTeamSessionType} from '../../api/teamSessionAPI';
import FormRadioGroup from '../shared/FormRadioGroup';
import {SessionTypes} from '../../enums';
import {Control, FieldValues, useForm} from 'react-hook-form';

interface Props {
  teamSessionId: number;
  sessionVideoId: number;
  type: SessionTypes;
  onSuccess: () => void;
}

const AdminChangeTeamSessionType: React.FC<Props> = ({
  teamSessionId,
  sessionVideoId,
  type,
  onSuccess,
}) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const {handleSubmit, control} = useForm<{type: SessionTypes}>({
    defaultValues: {
      type,
    },
  });

  const [changeInProgress, setChangeInProgress] = useState(false);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(!open), [open]);

  const handleChangeTeamSessionType = useCallback(
    async (data: {type: SessionTypes}) => {
      const confirmed = window.confirm(
        t('admin.changeTeamSessionTypeConfirm') as string,
      );
      if (confirmed) {
        setChangeInProgress(true);
        const res = await adminChangeTeamSessionType(teamSessionId, data.type);
        if (!res?.success) {
          window.alert(res?.error ?? t('admin.changeTeamSessionTypeError'));
          setChangeInProgress(false);
          return;
        }

        // reprocess video
        if (sessionVideoId) {
          dispatch(processSessionVideoAsync({sessionVideoId}));
        }
        setChangeInProgress(false);
        onSuccess();
      }
    },
    [dispatch, onSuccess, sessionVideoId, t, teamSessionId],
  );

  return (
    <Box sx={{my: 2}}>
      <Typography variant="h4" sx={{mb: 2}}>
        {t('admin.changeTeamSessionType')}
      </Typography>
      {open && (
        <form
          onSubmit={handleSubmit(handleChangeTeamSessionType)}
          className="form">
          <FormRadioGroup
            control={control as unknown as Control<FieldValues>}
            name="type"
            options={[
              {
                value: SessionTypes.game,
                label: t('sessionFilter.game'),
              },
              {
                value: SessionTypes.practice,
                label: t('sessionFilter.practice'),
              },
              {
                value: SessionTypes.skills,
                label: t('sessionFilter.skills'),
              },
            ]}
            radioSx={{flexDirection: 'column'}}
          />
          <Box sx={{mt: 2, display: 'flex', gap: 2}}>
            <Button
              sx={{mb: 2}}
              disabled={changeInProgress}
              type="submit"
              variant="contained">
              {t('general.submit')}
            </Button>
          </Box>
        </form>
      )}
      <Button
        variant="outlined"
        onClick={handleOpen}
        disabled={changeInProgress}>
        {open ? t('general.cancel') : t('general.continue')}
      </Button>
    </Box>
  );
};

export default AdminChangeTeamSessionType;
